import {
    ContentTypeEnumSchema,
    MessageParticipantRole,
    MessageType,
    ResponseMessage,
} from '@amzn/ring-cs-chatbot-service-model';
import { ManagedSession } from './ManagedSession';

export type PendingMessage = {
    reasoning?: string;
    content: string;
};

type PendingMessageContainer = {
    pendingMessage: PendingMessage;
    timeoutId: NodeJS.Timeout;
    messageId: string;
    parts: string[];
};

export class PendingMessageManager {
    // 30 seconds - timeout for pending message resolution
    private static readonly TIMEOUT_INTERVAL = 30000;

    private pendingMessageContainer?: PendingMessageContainer;

    private onPendingChangeHandlers: Set<(pendingMessage?: PendingMessage) => void> = new Set();

    // Track received message IDs to prevent processing stale pending events
    private receivedMessageIds: Set<string> = new Set();

    constructor (private session: ManagedSession) {
        // Listen for pending message events and regular messages
        this.session.onMessage({ handler: this.handleMessage.bind(this) });
    }

    public onPendingChange (handler: (pendingMessage?: PendingMessage) => void) {
        this.onPendingChangeHandlers.add(handler);

        handler(this.pendingMessageContainer?.pendingMessage);

        return () => {
            this.onPendingChangeHandlers.delete(handler);
        };
    }

    private syncPendingMessage () {
        const pending = this.pendingMessageContainer?.pendingMessage;
        this.onPendingChangeHandlers.forEach(handler => handler(pending));
    }

    private setPendingMessage (pendingMessageId: string) {
        // Check for existing pending message
        if (this.pendingMessageContainer) {
            if (this.pendingMessageContainer.messageId === pendingMessageId) {
                return;
            } else {
                this.removePendingMessage();
            }
        }

        // Set new timeout
        const timeoutId = setTimeout(() => {
            console.warn('Pending message timed out:', pendingMessageId);
            this.removePendingMessage();
        }, PendingMessageManager.TIMEOUT_INTERVAL);

        this.pendingMessageContainer = {
            pendingMessage: {
                content: '',
                reasoning: '',
            },
            messageId: pendingMessageId,
            timeoutId,
            parts: [],
        };

        this.syncPendingMessage();
    }

    private removePendingMessage () {
        if (this.pendingMessageContainer) {
            clearTimeout(this.pendingMessageContainer.timeoutId);
            this.pendingMessageContainer = undefined;
            this.syncPendingMessage();
        }
    }

    private handleMessage (message: ResponseMessage) {
        this.receivedMessageIds.add(message.messageId);

        // Handle pending message events
        if (message.contentType === ContentTypeEnumSchema.enum['application/chat.event.message.pending'] && message.content) {
            const eventData = JSON.parse(message.content);
            const pendingMessageId = eventData.pendingMessageId;
            // If the pending message ID is not set or we've already received the message, return
            if (!pendingMessageId || this.receivedMessageIds.has(pendingMessageId)) {
                return;
            }
            this.setPendingMessage(pendingMessageId);
        }

        if (message.type === MessageType.MESSAGE) {
            if (message.participantRole === MessageParticipantRole.CUSTOMER) {
                // Handle customer messages with responseMessageId attribute
                if (message.attributes?.responseMessageId) {
                    this.setPendingMessage(message.attributes.responseMessageId);
                }
            } else {
                // Clear pending message when non-customer message arrives
                this.removePendingMessage();
            }
        }

        // Handle streaming events
        if (message.contentType === ContentTypeEnumSchema.enum['application/chat.event.stream'] && message.content) {
            const streamData = JSON.parse(message.content);
            const { pendingMessageId, text, reasoning, seq } = streamData;

            const container = this.pendingMessageContainer;
            if (!container || container.messageId !== pendingMessageId) {
                return;
            }

            // join valid parts
            container.parts[seq] = text;
            let joinedContent = '';
            for (const part of container.parts) {
                if (part === undefined) break;
                joinedContent += part;
            }

            // Update content: reset on seq 0, otherwise append
            container.pendingMessage = {
                reasoning: reasoning ?? container.pendingMessage.reasoning,
                content: joinedContent,
            };

            // Refresh timeout
            clearTimeout(container.timeoutId);
            container.timeoutId = setTimeout(() => this.removePendingMessage(), PendingMessageManager.TIMEOUT_INTERVAL);

            this.syncPendingMessage();
        }
    }
}
